import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import TableAllData from "components/Content/CustomTable/TableAllData";
import { Box, Button, IconButton } from "@mui/material";
import ButtonStyle from "components/Button/ButtonStyle.module.css";

import { apiGet } from "app/Services/ApiService";
import { apiUrls } from "common/ApiUrls";
import { PrivateRoutesContext } from "app/PrivateRoutesContext";
import AximTooltip from "components/AximTooltip";

import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';

import { Utils } from "common/Utils";
import ConfirmDialog from "components/Content/Modal/ConfirmDialog";
import StatusDetailsDialog from "components/Content/Modal/StatusDetailsDialog";

export default function FileUploadTable({ uploadFiles, isLoading, headerContents, doDisplayErrorToggle }) {
    const { companyId, error } = useContext(PrivateRoutesContext);

    const [doShowConfirm, setDoShowConfirm] = useState(false);

    const [doShowStatusDetails, setDoShowStatusDetails] = useState(false);
    const [statusTitle, setStatusTitle] = useState("");
    const [statuses, setStatuses] = useState([]);

    const [confirmCloseAction, setConfirmCloseAction] = useState(null);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [confirmButtonsDisabled, setConfirmButtonsDiabled] = useState(false);

    useEffect(() => {
      apiGet(apiUrls.getStatuses("USERUPLOAD"))
        .then((response) => {
          setStatuses(response.data);
      })
      .catch(function (e) {
          setStatuses([]);
      });
    }, []);

    const columns = useMemo(() => [
        {
          id: 'expander',
          Header: "",
          accessor: "userUploadId",
          Cell: e => {
            return (
              <>
                <AximTooltip title="Download File">
                  <IconButton onClick={() => promptDownloadFile(`${e.value}`, `${e.row.values.fileName}`)} color="primary"><CloudDownloadOutlinedIcon /></IconButton>
                </AximTooltip>
                {doDisplayErrorToggle && e.row.original.status === "Completed" ?
                  e.row.original.errorCount > 0 ?
                    <AximTooltip title={e.row.isExpanded ? "Hide Errors" : "Show Errors"}>
                      <IconButton onClick={() => toggleShowErrors(e,`${e.row.original.fileId}`)} color="error"><CheckCircleOutlineIcon /></IconButton>
                    </AximTooltip> :
                    <IconButton color="success"><CheckCircleIcon /></IconButton>
                  : null
                }
              </>
            )
          },
          disableFilters: true,
        },
        {
          Header: "File Name",
          accessor: "fileName",
        },
        {
          Header: "File Type",
          accessor: "fileType",
        },
        {
          Header: () => {
            return (
              <>
                Status
                <IconButton onClick={showStatuses} className="py-0" color="primary"><ListAltOutlinedIcon /></IconButton>
              </>
            )
          },
          accessor: "status",
        },
        {
          Header: "Upload Date",
          accessor: (cell) => Utils.formatDate(cell.uploadDate),
          Cell: ({row}) => (
            <Box>
              {Utils.formatDate(row.original.uploadDate)}
            </Box>
          ),
          sortType: "dateSort",
        },
        {
          Header: "Upload By",
          accessor: "uploadUser",
        },
    ]);

    const showStatuses = e => {
      e.stopPropagation();

      setStatusTitle("User Upload Statuses");
      setDoShowStatusDetails(true);
    }

    const renderRowSubComponent = useCallback(
        ({ row, rowProps, visibleColumns }) => (
            <ErrorSubRowAsync
                row={row}
                rowProps={rowProps}
                visibleColumns={visibleColumns}
            />
        ), []
    );

    const toggleShowErrors = (row, fileId) => {
        row.row.toggleRowExpanded();
    }

    const promptDownloadFile = (fileId, fileName) => {
      setConfirmCloseAction(() => (result) => downloadUserFile(result, fileId, fileName));
      setConfirmTitle('Download File');
      setConfirmText(`Are you sure you would like to download file?`);
      setDoShowConfirm(true);
    }

    const downloadUserFile = (isConfirmed, fileId, fileName) => {
      if (isConfirmed) {
        setConfirmButtonsDiabled(true);

        Utils.downloadFile(apiUrls.downloadFile(companyId, fileId), fileName, null, 
          () => {
            error.setTitle("An Error Occurred");
            error.setText("An Error occurred downloading the file.");
            error.setDoShowError(true);
          },
          () => {
            setDoShowConfirm(false);
            setConfirmButtonsDiabled(false);
          });
      }
      else {
        setDoShowConfirm(false);
      }
    }

    return (
      <Box>
        <TableAllData columns={columns} data={uploadFiles} isLoading={isLoading} renderRowSubComponent={renderRowSubComponent} headerContents={headerContents} />
        
        <ConfirmDialog
          doShowConfirm={doShowConfirm}
          handleClose={(result) => confirmCloseAction(result)}
          confirmTitle={confirmTitle}
          confirmText={confirmText}
          buttonsDisabled={confirmButtonsDisabled} />

        <StatusDetailsDialog doShow={doShowStatusDetails} handleClose={() => setDoShowStatusDetails(false)} title={statusTitle} statuses={statuses} />
      </Box>
    );
}

function ErrorSubRowAsync({ row, rowProps, visibleColumns }) {
    const { companyId, error } = useContext(PrivateRoutesContext);

    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [data, setData] = useState([]);
  
    useEffect(() => {
      apiGet(apiUrls.getFileErrors(row.original.fileId)).then(
        (response) => {
          setData(response.data);
          setLoading(false);
        }
      ).catch(
        (response) => {
          setHasError(true);
          setLoading(false);
        }
      );
    }, []);
  
    const headerContents =
        <Button
          variant="outlined"
          color="primary"
          className={ButtonStyle.CsubmitBtn}
          onClick={() => downloadErrors()}
          startIcon={<DownloadOutlinedIcon />}
        >
          Download Errors
        </Button>
  
    const columns = useMemo(() => {
      var columns = [];
  
      if (data.length > 0) {
        for (var field in data[0]) {
          if (field !== "Error Description") {
            columns.push({Header: field, accessor: field});
          }
        }
      }
  
      return columns;
    }, [data]);
  
    const downloadErrors = () => {
      Utils.downloadFile(apiUrls.getDownloadFileErrors(row.original.fileId), "errors.csv", null,
      () => {
        error.setTitle("An Error Occurred");
        error.setText("An Error occurred downloading the file.");
        error.setDoShowError(true);
      });
    }
  
    const errorDescription = (row, rowProps, visibleColumns) => {
      return (
        <Box>
          <Box component="span" className="px-2">
            <ErrorOutlineOutlinedIcon color="error"/>
          </Box>
          {row.row.original["Error Description"]}
        </Box>)
    }
  
    return (
      hasError ? 
        <Box>Unable to load errors</Box> :
        <TableAllData columns={columns} data={data} isLoading={loading} renderRowSubComponent={errorDescription} isAlwaysExpanded={true} headerContents={headerContents} />
    );
  }